import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Save, Loader2, Clock, Sun, Moon, Sunrise } from 'lucide-react';
import { settingsApi } from '../../lib/api';

interface TradingSchedule {
  pre_market_enabled: boolean;
  regular_hours_enabled: boolean;
  after_hours_enabled: boolean;
  overnight_enabled: boolean;
  pre_market_start: string;
  pre_market_end: string;
  regular_start: string;
  regular_end: string;
  after_hours_start: string;
  after_hours_end: string;
  trading_days: number[];
  pre_market_max_position_pct: number;
  after_hours_max_position_pct: number;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT: TradingSchedule = {
  pre_market_enabled: true,
  regular_hours_enabled: true,
  after_hours_enabled: true,
  overnight_enabled: true,
  pre_market_start: '04:00',
  pre_market_end: '09:30',
  regular_start: '09:30',
  regular_end: '16:00',
  after_hours_start: '16:00',
  after_hours_end: '20:00',
  trading_days: [0, 1, 2, 3, 4],
  pre_market_max_position_pct: 0.05,
  after_hours_max_position_pct: 0.05,
};

export default function TradingScheduleTab() {
  const [schedule, setSchedule] = useState<TradingSchedule>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await settingsApi.getConfigs();
      const sched = data?.configs?.trading_schedule as TradingSchedule | undefined;
      if (sched) setSchedule({ ...DEFAULT, ...sched });
    } catch (e: any) {
      setError('Failed to load schedule settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const result = await settingsApi.saveConfigs({ trading_schedule: schedule }) as any;
      if (result?.status === 'saved_to_memory_only') {
        setError('Saved to memory only — Firestore unavailable. Settings will reset on server restart.');
      } else {
        setMsg('Trading schedule saved.');
        setTimeout(() => setMsg(''), 4000);
      }
    } catch (e: any) {
      const detail = e?.detail || e?.message || 'Save failed';
      setError(detail.includes('403') || detail.includes('role') ? 'Permission denied — Admin or Trader role required.' : detail);
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (d: number) => {
    setSchedule(s => ({
      ...s,
      trading_days: s.trading_days.includes(d)
        ? s.trading_days.filter(x => x !== d)
        : [...s.trading_days, d].sort(),
    }));
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-muted-foreground py-8">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Trading Days */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Clock className="w-4 h-4" />Trading Days</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {DAY_LABELS.map((label, idx) => (
              <button
                key={idx}
                onClick={() => toggleDay(idx)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors
                  ${schedule.trading_days.includes(idx)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted text-muted-foreground border-border hover:bg-accent'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pre-Market */}
      <Card className={schedule.pre_market_enabled ? 'border-amber-500/40' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sunrise className="w-4 h-4 text-amber-500" />
              Pre-Market Session
              {schedule.pre_market_enabled && <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">Active</Badge>}
            </CardTitle>
            <Switch
              checked={schedule.pre_market_enabled}
              onCheckedChange={v => setSchedule(s => ({ ...s, pre_market_enabled: v }))}
            />
          </div>
          <CardDescription>Enables scanning and order submission during pre-market hours (4:00–9:30 AM ET). Only bots tagged <code>gap</code>, <code>premarket</code>, or <code>catalyst</code> will trade in this window.</CardDescription>
        </CardHeader>
        {schedule.pre_market_enabled && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Start (ET)</label>
                <Input type="time" value={schedule.pre_market_start}
                  onChange={e => setSchedule(s => ({ ...s, pre_market_start: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">End (ET)</label>
                <Input type="time" value={schedule.pre_market_end}
                  onChange={e => setSchedule(s => ({ ...s, pre_market_end: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Max Position Size (% of equity)</label>
              <Input type="number" min={0.01} max={0.25} step={0.01}
                value={(schedule.pre_market_max_position_pct * 100).toFixed(0)}
                onChange={e => setSchedule(s => ({ ...s, pre_market_max_position_pct: parseFloat(e.target.value) / 100 }))} />
              <p className="text-xs text-muted-foreground mt-1">Caps individual position size during pre-market. 5% is conservative; raise carefully.</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Regular Hours */}
      <Card className={schedule.regular_hours_enabled ? 'border-green-500/40' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sun className="w-4 h-4 text-green-500" />
              Regular Hours
              {schedule.regular_hours_enabled && <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Active</Badge>}
            </CardTitle>
            <Switch
              checked={schedule.regular_hours_enabled}
              onCheckedChange={v => setSchedule(s => ({ ...s, regular_hours_enabled: v }))}
            />
          </div>
          <CardDescription>NYSE/NASDAQ regular session. All bots are eligible unless blocked by the intraday regime gate.</CardDescription>
        </CardHeader>
        {schedule.regular_hours_enabled && (
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Start (ET)</label>
                <Input type="time" value={schedule.regular_start}
                  onChange={e => setSchedule(s => ({ ...s, regular_start: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">End (ET)</label>
                <Input type="time" value={schedule.regular_end}
                  onChange={e => setSchedule(s => ({ ...s, regular_end: e.target.value }))} />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* After Hours */}
      <Card className={schedule.after_hours_enabled ? 'border-indigo-500/40' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Moon className="w-4 h-4 text-indigo-400" />
              After-Hours Session
              {schedule.after_hours_enabled && <Badge className="bg-indigo-500/20 text-indigo-600 border-indigo-500/30">Active</Badge>}
            </CardTitle>
            <Switch
              checked={schedule.after_hours_enabled}
              onCheckedChange={v => setSchedule(s => ({ ...s, after_hours_enabled: v }))}
            />
          </div>
          <CardDescription>Post-close extended hours. Only bots tagged <code>after-hours</code> will trade. Orders are routed as limit orders.</CardDescription>
        </CardHeader>
        {schedule.after_hours_enabled && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Start (ET)</label>
                <Input type="time" value={schedule.after_hours_start}
                  onChange={e => setSchedule(s => ({ ...s, after_hours_start: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">End (ET)</label>
                <Input type="time" value={schedule.after_hours_end}
                  onChange={e => setSchedule(s => ({ ...s, after_hours_end: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Max Position Size (% of equity)</label>
              <Input type="number" min={0.01} max={0.25} step={0.01}
                value={(schedule.after_hours_max_position_pct * 100).toFixed(0)}
                onChange={e => setSchedule(s => ({ ...s, after_hours_max_position_pct: parseFloat(e.target.value) / 100 }))} />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Overnight (24/5) */}
      <Card className={schedule.overnight_enabled ? 'border-violet-500/40' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-violet-400" />
              Overnight (24/5)
              {schedule.overnight_enabled && <Badge className="bg-violet-500/20 text-violet-600 border-violet-500/30">Active</Badge>}
            </CardTitle>
            <Switch
              checked={schedule.overnight_enabled}
              onCheckedChange={v => setSchedule(s => ({ ...s, overnight_enabled: v }))}
            />
          </div>
          <CardDescription>Alpaca 24/5 overnight window (8:00 PM–4:00 AM ET, Sun–Thu). Only bots tagged <code>overnight</code> or <code>extended</code> trade here. Orders are routed as limit orders.</CardDescription>
        </CardHeader>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-4">
        <Button onClick={save} disabled={saving} className="flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Schedule
        </Button>
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>
    </div>
  );
}
